// src/context/UserContext.jsx
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import toast from "../utils/toast";
import authService from "../services/authService";
import apiClient from "../services/apiClient";
import { canSeeAgencyAdmin, isAgencyPlan, getPlanDisplayName, hasFeatureFlag } from "../config/subscriptionConfig";

// Dev mock — imported at module level so Vite can tree-shake in production.
// getMockUser() returns null when VITE_DEV_MOCK_USER is not set.
import { getMockUser, MOCK_USAGE } from '../dev/mockUsers.js';

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [usageSummary, setUsageSummary] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);

  const logout = async () => {
    setUser(null);
    setUsageSummary(null);
    await authService.logout();
  };

  /**
   * Fetch itinerary usage for the current agency.
   * Called after user loads successfully.
   * Kept as a separate endpoint per product decision — allows on-demand refresh
   * without re-fetching the full user context (e.g. after creating a new trip).
   */
  const fetchUsageSummary = useCallback(async () => {
    setLoadingUsage(true);
    try {
      const { data } = await apiClient.get('/api/me/usage');
      setUsageSummary(data);
    } catch (err) {
      // Non-fatal — usage display degrades gracefully if this fails
      console.warn('Could not load usage summary:', err?.response?.status ?? err.message);
      setUsageSummary(null);
    } finally {
      setLoadingUsage(false);
    }
  }, []);

  const fetchUser = useCallback(async () => {
    try {
      // ── Dev mock bypass ─────────────────────────────────────────────────────
      // When VITE_DEV_MOCK_USER is set in .env.local, skip the real API call
      // and return a canned user object. Useful for UI development without a
      // local backend. Set VITE_DEV_MOCK_USER to: agency_admin | solo_agent |
      // sub_agent | parkpro_admin
      if (import.meta.env.DEV) {
        const mockUser = getMockUser();
        if (mockUser) {
          setUser(mockUser);
          setUsageSummary(MOCK_USAGE);
          setLoadingUser(false);
          setLoadingUsage(false);
          return;
        }
      }
      // ────────────────────────────────────────────────────────────────────────

      // Small delay to ensure cookies are set after login redirect
      await new Promise(resolve => setTimeout(resolve, 100));

      const data = await authService.getCurrentUser();

      if (!data) {
        setUser(null);
        return;
      }

      // Handle status-based blocks (pending approval, denied, suspended)
      if (data.status && ['pending', 'denied', 'suspended'].includes(data.status)) {
        console.warn('User account status issue:', data.status, data.message);
        setUser(null);
        toast.error(data.message || 'Account access restricted');
        return;
      }

      setUser(data);

      // Persist safe fields for offline/reload use
      const safeUserData = authService.getSafeUserData(data);
      if (safeUserData) {
        localStorage.setItem('user', JSON.stringify(safeUserData));
      }

      // Fetch usage in parallel after user is set — non-blocking
      fetchUsageSummary();
    } catch (err) {
      console.error('Error fetching user:', err);
      setUser(null);
      localStorage.removeItem('user');
    } finally {
      setLoadingUser(false);
    }
  }, [fetchUsageSummary]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // ─── Derived access values (computed once here, consumed everywhere) ─────────
  //
  // These are the single authoritative answers to access questions.
  // Components should read these rather than re-computing from user fields.

  /** True if this user's agency is on an agency-tier plan (AgencyMaxUsers > 1). */
  const userIsAgencyPlan = isAgencyPlan(user);

  /**
   * True if this user should see the "My Agency" admin section.
   * Requires: not an agentUser sub-agent AND on an agency-tier plan.
   */
  const userCanSeeAgencyAdmin = canSeeAgencyAdmin(user);

  /** Human-readable plan name for UI display (e.g. "Agency+"). */
  const planDisplayName = getPlanDisplayName(user);

  /**
   * Feature flags returned by /api/me (computed server-side from subscription plan).
   * Use hasFeatureFlag(user, 'branding') or read these directly.
   * Do NOT recompute feature eligibility in components.
   */
  const featureFlags = user?.featureFlags ?? {};

  return (
    <UserContext.Provider
      value={{
        // Raw user data
        user,
        loadingUser,
        setUser,
        logout,
        fetchUser,

        // Itinerary usage (separate endpoint — refreshable after trip creation)
        usageSummary,
        loadingUsage,
        fetchUsageSummary,

        // Derived access values — use these in components and hooks
        isAgencyPlan: userIsAgencyPlan,
        canSeeAgencyAdmin: userCanSeeAgencyAdmin,
        planDisplayName,
        featureFlags,

        // Convenience: check a single feature flag without importing subscriptionConfig
        hasFeatureFlag: (flag) => hasFeatureFlag(user, flag),
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
